import React, { createContext, memo, useContext } from 'react';

export interface IStateProps<S> {
  stateProps?: S;
}

export interface IGenericMakeContext<C, P> {
  useState(props?: { initialState?: Omit<P, 'children'> }): C;
  initialState?: Omit<P, 'children'>;
  initialContext: C;
  memoiseProvider?: boolean;
  useRenderChildrenCondition?: (state?: C) => boolean;
}

export interface IProviderProps {
  children: React.ReactNode;
}

export interface IUseSelector<G, C> {
  (context: C): G;
}

const genericMakeContext = <C, P extends IProviderProps, G = unknown>({
  initialState,
  initialContext,
  useState,
  memoiseProvider = true,
  useRenderChildrenCondition = () => true,
}: IGenericMakeContext<C, P>) => {
  const Context = createContext<C>(initialContext);
  const useContextWrapper = (): C => useContext(Context);

  const useAppSelector = (selector: IUseSelector<G, C>) => {
    const context = useContextWrapper();
    return selector(context);
  };

  const Provider = ({ children, ...rest }: P) => {
    const mergedInitialState = { ...rest, ...initialState };
    const state = useState({ initialState: mergedInitialState });
    const renderChildren = useRenderChildrenCondition(state);
    return (
      <Context.Provider value={state}>
        {renderChildren ? children : null}
      </Context.Provider>
    );
  };

  Provider.displayName = `Provider - ${useState.name}`;

  return {
    Context,
    Provider: memoiseProvider ? memo(Provider) : Provider,
    useContext: useContextWrapper,
    useAppSelector,
  };
};

export default genericMakeContext;
